import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { FormAlert } from '../components/form';
import CurrencyViewToggle from '../components/CurrencyViewToggle';
import PillGroup from '../components/PillGroup';
import { hasMultimoneda } from '../utils/multimoneda';
import { formatMoney } from '../utils/money';
import {
  VISTA_CONSOLIDADO,
  displayCurrency,
  shouldShowCurrencyToggle,
  vistaHint,
} from '../utils/currencyView';
import useStickyMonedas from '../hooks/useStickyMonedas';
import { shouldShowIngresosPorPais } from '../utils/dashboardFeatures';

const PERIODOS = [
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mes' },
  { id: 'trimestre', label: 'Trimestre' },
  { id: 'año', label: 'Año' },
  { id: 'todas', label: 'Todo' },
];

const PERIODO_TEXTO = {
  semana: 'la última semana',
  mes: 'este mes',
  trimestre: 'este trimestre',
  año: 'este año',
  todas: 'todo el historial',
};

const BAR_COLORS = ['#1F80FF', '#5CA3FF', '#004AAD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8', '#94A3B8'];

const SORT_OPTIONS = [
  { id: 'total', label: 'Por ingresos' },
  { id: 'cantidad', label: 'Por ventas' },
];

function countryLabel(row) {
  return row.pais || row._id || 'Sin país';
}

const PaisesDetalle = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();

  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('año');
  const [sortBy, setSortBy] = useState('total');
  const [currencyVista, setCurrencyVista] = useState(VISTA_CONSOLIDADO);

  const currency = displayCurrency(currencyVista);
  const canView = shouldShowIngresosPorPais(user);

  const fetchPaises = useCallback(async () => {
    if (!canView) return;
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(
        `/api/invoices-dashboard/ingresos-por-pais?periodo=${filtroFecha}&limit=200&vista=${encodeURIComponent(currencyVista)}`
      );
      if (!res.ok) throw new Error('Error al cargar países');
      const data = await res.json();
      setPaises(Array.isArray(data) ? data : []);
    } catch {
      setError('No se pudieron cargar los ingresos por país.');
    } finally {
      setLoading(false);
    }
  }, [token, filtroFecha, currencyVista, secureFetch, canView]);

  useEffect(() => {
    if (canView) fetchPaises();
  }, [fetchPaises, canView]);

  useEffect(() => {
    if (!canView) navigate('/inicio', { replace: true });
  }, [canView, navigate]);

  const availableMonedas = useStickyMonedas(paises, currencyVista);
  const showCurrencyToggle = hasMultimoneda(user) && shouldShowCurrencyToggle(availableMonedas);

  const ranked = useMemo(() => {
    const list = [...paises].filter((p) => (p.total || 0) !== 0);
    return list.sort((a, b) => {
      if (sortBy === 'cantidad') {
        return (b.cantidadFacturas || 0) - (a.cantidadFacturas || 0);
      }
      return (b.total || 0) - (a.total || 0);
    });
  }, [paises, sortBy]);

  const totalIngresos = ranked.reduce((sum, p) => sum + (p.total || 0), 0);
  const totalPaises = ranked.length;
  const totalVentas = ranked.reduce((sum, p) => sum + (p.cantidadFacturas || 0), 0);
  const topPais = ranked[0];
  const topShare = totalIngresos > 0 && topPais ? (topPais.total / totalIngresos) * 100 : 0;
  const top3Share = totalIngresos > 0
    ? (ranked.slice(0, 3).reduce((sum, p) => sum + (p.total || 0), 0) / totalIngresos) * 100
    : 0;

  const barChart = useMemo(() => {
    if (!ranked.length) return { rows: [], total: 0 };
    const total = totalIngresos;
    const top = ranked.slice(0, 8);
    const resto = ranked.slice(8).reduce((sum, p) => sum + (p.total || 0), 0);
    const rows = top.map((item, index) => {
      const name = countryLabel(item);
      const amount = item.total || 0;
      return {
        pais: name.length > 14 ? `${name.slice(0, 13)}…` : name,
        paisFull: name,
        total: amount,
        cantidad: item.cantidadFacturas || 0,
        pct: total > 0 ? (amount / total) * 100 : 0,
        color: BAR_COLORS[index % BAR_COLORS.length],
      };
    });
    if (resto > 0) {
      rows.push({
        pais: 'Resto',
        paisFull: 'Resto',
        total: resto,
        cantidad: ranked.slice(8).reduce((sum, p) => sum + (p.cantidadFacturas || 0), 0),
        pct: total > 0 ? (resto / total) * 100 : 0,
        color: BAR_COLORS[7],
      });
    }
    return { rows, total };
  }, [ranked, totalIngresos]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return ranked;
    return ranked.filter((p) => countryLabel(p).toLowerCase().includes(q));
  }, [ranked, searchTerm]);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  const formatCurrency = (amount) => formatMoney(amount, currency);

  const kpis = [
    { label: 'Países', value: loading ? '—' : String(totalPaises), hint: 'con ventas en el período' },
    {
      label: 'Ingresos',
      value: loading ? '—' : formatCurrency(totalIngresos),
      hint: currencyVista === VISTA_CONSOLIDADO ? 'todas las monedas' : `solo ${currency}`,
    },
    { label: 'Ventas', value: loading ? '—' : String(totalVentas), hint: 'registros en el período' },
    {
      label: 'Top 1',
      value: loading ? '—' : `${topShare.toFixed(0)}%`,
      hint: topPais ? countryLabel(topPais) : 'sin datos',
    },
    {
      label: 'Top 3',
      value: loading ? '—' : `${top3Share.toFixed(0)}%`,
      hint: 'de los ingresos',
    },
  ];

  const chartHeight = Math.min(420, Math.max(220, barChart.rows.length * 36));

  if (!canView) return null;

  return (
    <div className={modoOscuro ? 'w-full bg-black text-white' : 'w-full bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 lg:py-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/inicio')}
              className={`p-2 rounded-lg border shrink-0 ${modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              aria-label="Volver al inicio"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
                Ingresos por país
              </h1>
              <p className={`text-sm mt-1 ${textSec}`}>
                De dónde vinieron las ventas de {PERIODO_TEXTO[filtroFecha] || 'este período'}
                {currencyVista === VISTA_CONSOLIDADO && showCurrencyToggle ? ' · total' : currencyVista !== VISTA_CONSOLIDADO ? ` · ${currency}` : ''}
              </p>
            </div>
          </div>
          {showCurrencyToggle && (
            <div className="flex flex-col items-end gap-1">
              <CurrencyViewToggle
                value={currencyVista}
                onChange={setCurrencyVista}
                monedas={availableMonedas}
                modoOscuro={modoOscuro}
              />
              <p className={`text-[11px] ${textMuted}`}>{vistaHint(currencyVista)}</p>
            </div>
          )}
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
          {kpis.map((card) => (
            <div key={card.label} className={`rounded-2xl border p-3.5 sm:p-4 ${box}`}>
              <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
              <div className={`text-lg sm:text-xl font-semibold mt-0.5 truncate ${textPri}`}>{card.value}</div>
              <div className={`text-[11px] mt-0.5 truncate ${textMuted}`}>{card.hint}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <section className={`lg:col-span-5 rounded-2xl border p-4 sm:p-5 ${box}`}>
            <div className="mb-3">
              <h2 className={`text-sm font-semibold ${textPri}`}>Top países</h2>
              <p className={`text-xs mt-0.5 ${textMuted}`}>Participación sobre el total del período</p>
            </div>
            {loading ? (
              <div className={`rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} style={{ height: chartHeight }} />
            ) : barChart.rows.length === 0 ? (
              <div className="h-[240px] flex flex-col items-center justify-center text-center">
                <GlobeAltIcon className={`w-10 h-10 mb-2 ${textMuted}`} />
                <p className={`text-sm font-medium ${textPri}`}>Todavía no hay ventas por país</p>
                <p className={`text-xs mt-1 ${textMuted}`}>Cuando registres una venta con país, aparece acá.</p>
              </div>
            ) : (
              <div style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barChart.rows}
                    layout="vertical"
                    margin={{ top: 4, right: 40, left: 4, bottom: 0 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="pais"
                      width={76}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: modoOscuro ? '#9CA3AF' : '#6B7280', fontSize: 11 }}
                    />
                    <Tooltip
                      cursor={{ fill: modoOscuro ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const row = payload[0]?.payload || {};
                        return (
                          <div
                            className={`rounded-xl border px-3 py-2 text-xs shadow-lg ${
                              modoOscuro ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                            }`}
                          >
                            <p className="font-semibold">{row.paisFull || row.pais}</p>
                            <p className={modoOscuro ? 'text-gray-200' : 'text-gray-600'}>
                              {row.cantidad ? `${row.cantidad} venta${row.cantidad !== 1 ? 's' : ''} · ` : ''}
                              {formatCurrency(row.total)} · {Number(row.pct || 0).toFixed(1)}%
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={16} isAnimationActive={false}>
                      {barChart.rows.map((row) => (
                        <Cell key={row.paisFull || row.pais} fill={row.color} />
                      ))}
                      <LabelList
                        dataKey="pct"
                        position="right"
                        formatter={(value) => `${Number(value).toFixed(0)}%`}
                        style={{ fill: modoOscuro ? '#D1D5DB' : '#4B5563', fontSize: 10, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <section className={`lg:col-span-7 rounded-2xl border overflow-hidden ${box}`}>
            <div className={`p-4 sm:p-5 border-b space-y-3 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <PillGroup value={filtroFecha} onChange={setFiltroFecha} options={PERIODOS} modoOscuro={modoOscuro} />
                <PillGroup value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} modoOscuro={modoOscuro} />
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar país"
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
                />
              </div>
            </div>

            <FormAlert type="error" className="mx-4 mt-4">{error}</FormAlert>

            {loading ? (
              <div className="p-4 sm:p-5 space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`h-14 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <GlobeAltIcon className={`w-10 h-10 mx-auto mb-2 ${textMuted}`} />
                <p className={`text-sm font-medium ${textPri}`}>No hay países en este recorte</p>
                <p className={`text-xs mt-1 ${textMuted}`}>
                  {searchTerm ? 'Probá otro nombre.' : 'Registrá ventas con país para ver el desglose.'}
                </p>
              </div>
            ) : (
              <ul className={modoOscuro ? 'divide-y divide-gray-800' : 'divide-y divide-gray-100'}>
                {filtered.map((pais, index) => {
                  const nombre = countryLabel(pais);
                  const porcentaje = totalIngresos > 0 ? (pais.total / totalIngresos) * 100 : 0;
                  const color = BAR_COLORS[index % BAR_COLORS.length];
                  return (
                    <li key={`${nombre}-${index}`}>
                      <div
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 ${
                          modoOscuro ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`text-xs font-semibold w-5 shrink-0 tabular-nums ${textMuted}`}>{index + 1}</span>
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                            modoOscuro ? 'bg-[#1F80FF]/20' : 'bg-[#1F80FF]/10'
                          }`}
                        >
                          <GlobeAltIcon className="w-4 h-4 text-[#1F80FF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-semibold truncate ${textPri}`}>{nombre}</div>
                          <div className={`text-[11px] truncate flex items-center gap-1 ${textMuted}`}>
                            <DocumentTextIcon className="w-3.5 h-3.5 shrink-0" />
                            {pais.cantidadFacturas || 0} venta{(pais.cantidadFacturas || 0) !== 1 ? 's' : ''}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.min(Math.max(porcentaje, 0), 100)}%`,
                                  backgroundColor: color,
                                }}
                              />
                            </div>
                            <span className={`text-[11px] font-semibold tabular-nums w-10 text-right ${textMuted}`}>
                              {porcentaje.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>
                            {formatCurrency(pais.total)}
                          </div>
                          <div className={`text-[11px] ${textMuted}`}>ingresos</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {!loading && filtered.length > 0 && (
          <p className={`text-xs ${textMuted}`}>
            Los montos respetan la misma vista de moneda que el inicio. El país se toma del campo país de cada venta.
          </p>
        )}
      </div>
    </div>
  );
};

export default PaisesDetalle;
